import type { Session } from '../types/types.ts';

export type SessionGroupKey = 'today' | 'yesterday' | 'lastSevenDays' | 'older';
export type SessionGroup = { key: SessionGroupKey; sessions: Session[] };

const GROUP_ORDER: SessionGroupKey[] = [
  'today',
  'yesterday',
  'lastSevenDays',
  'older',
];

function groupKeyOf(date: Date, now: Date): SessionGroupKey {
  const startOfDay = (daysAgo: number) =>
    new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - daysAgo,
    ).getTime();
  const time = date.getTime();
  if (time >= startOfDay(0)) {
    return 'today';
  }
  if (time >= startOfDay(1)) {
    return 'yesterday';
  }
  if (time >= startOfDay(6)) {
    return 'lastSevenDays';
  }
  return 'older';
}

export function groupSessions(
  sessions: Session[],
  now = new Date(),
): SessionGroup[] {
  const groups = new Map<SessionGroupKey, Session[]>();
  for (const session of sessions) {
    const key = groupKeyOf(new Date(session.lastMessageAt), now);
    groups.set(key, [...(groups.get(key) ?? []), session]);
  }
  return GROUP_ORDER.filter((key) => groups.has(key)).map((key) => ({
    key,
    sessions: groups.get(key) ?? [],
  }));
}

// Heute und Gestern: nur die Uhrzeit. Letzte 7 Tage und älter: Uhrzeit · Datum, älter mit Jahr
export function formatSessionTime(
  isoDate: string,
  group: SessionGroupKey,
  language: string,
): string {
  const date = new Date(isoDate);
  const time = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  if (group === 'today' || group === 'yesterday') {
    return time;
  }
  const day = new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    ...(group === 'older' && { year: 'numeric' }),
  }).format(date);
  return `${time} · ${day}`;
}

export function formatSessionDate(isoDate: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(isoDate));
}
